import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography, useTheme
} from '@mui/material';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { canUseConnections, isB2bTc, isThaklisDisabled } from '../../utils/roleUtils';
import {
  ACTIONS, FEATURES, describeMatrix, emptyMatrix, normaliseMatrix
} from '../../utils/permissions';

const f = { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' };

/**
 * RoleDialog — create or edit a per-typing-center custom role.
 *
 * The permission matrix is a feature × action grid. Rules baked into the grid so a
 * saved role is always coherent: granting Create/Edit or Delete also grants View, and
 * revoking View clears the other two. Column headers toggle a whole action, the last
 * column toggles a whole feature. Features the typing center doesn't have (Thaklis
 * features, Connections) are hidden but preserved on save.
 *
 * Props: open, onClose, onSave(payload) -> Promise, role (null = create), currentUser, saving
 */
const RoleDialog = ({ open, onClose, onSave, role, currentUser, saving = false }) => {
  const theme = useTheme();
  const isEdit = Boolean(role);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [matrix, setMatrix] = useState(emptyMatrix());
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName(role?.name || '');
    setDescription(role?.description || '');
    setMatrix(normaliseMatrix(role?.permissions));
    setError(null);
  }, [open, role]);

  // Rows visible for this typing center. Hidden features keep whatever value they have.
  const visibleFeatures = useMemo(() => FEATURES.filter((feat) => {
    if (feat.requiresThaklis && isThaklisDisabled(currentUser)) return false;
    if (feat.requiresConnections && !(isB2bTc(currentUser) || canUseConnections(currentUser))) return false;
    return true;
  }), [currentUser]);

  const setPerm = (feature, action, checked) => {
    setMatrix((prev) => {
      const row = { ...prev[feature], [action]: checked };
      if (action === 'read' && !checked) { row.write = false; row.delete = false; }
      if ((action === 'write' || action === 'delete') && checked) row.read = true;
      return { ...prev, [feature]: row };
    });
  };

  const setRowAll = (feature, checked) =>
    setMatrix((prev) => ({ ...prev, [feature]: { read: checked, write: checked, delete: checked } }));

  const setColumnAll = (action, checked) =>
    setMatrix((prev) => {
      const next = { ...prev };
      visibleFeatures.forEach(({ key }) => {
        const row = { ...next[key], [action]: checked };
        if (action === 'read' && !checked) { row.write = false; row.delete = false; }
        if ((action === 'write' || action === 'delete') && checked) row.read = true;
        next[key] = row;
      });
      return next;
    });

  const columnState = (action) => {
    const vals = visibleFeatures.map(({ key }) => matrix[key]?.[action] === true);
    const on = vals.filter(Boolean).length;
    return { checked: on > 0 && on === vals.length, indeterminate: on > 0 && on < vals.length };
  };

  const rowState = (feature) => {
    const p = matrix[feature] || {};
    const on = ['read', 'write', 'delete'].filter((a) => p[a]).length;
    return { checked: on === 3, indeterminate: on > 0 && on < 3 };
  };

  const validate = () => {
    if (!name.trim()) return 'Give the role a name.';
    return null;
  };

  const handleSave = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    try {
      // The role code is generated by the server from the name, unique within this typing center.
      const payload = { name: name.trim(), description: description.trim(), permissions: matrix };
      await onSave(payload);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Could not save the role.');
    }
  };

  const summary = describeMatrix(matrix);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: f.md, pb: 1 }}>
        {isEdit ? 'Edit Role' : 'Create Role'}
        <Typography sx={{ fontSize: f.xs, color: 'text.secondary', mt: 0.25 }}>
          Choose what people with this role can see and do. Staff members are assigned one role each.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2, fontSize: f.xs }} onClose={() => setError(null)}>{error}</Alert>}

        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 2fr' }} gap={1.5} mb={2}>
          <TextField
            label="Role name" value={name} onChange={(e) => setName(e.target.value)}
            size="small" required autoFocus placeholder="e.g. Processor"
            InputProps={{ sx: { fontSize: f.sm } }} InputLabelProps={{ sx: { fontSize: f.sm } }}
          />
          <TextField
            label="Description" value={description} onChange={(e) => setDescription(e.target.value)}
            size="small" placeholder="What this role is for (optional)"
            InputProps={{ sx: { fontSize: f.sm } }} InputLabelProps={{ sx: { fontSize: f.sm } }}
          />
        </Box>

        <TableContainer component={Box} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow sx={{ '& th': { fontSize: f.xs, py: 0.75 } }}>
                <TableCell>Feature</TableCell>
                {ACTIONS.map((a) => {
                  const st = columnState(a.key);
                  return (
                    <TableCell key={a.key} align="center" sx={{ width: 120 }}>
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <span>{a.label}</span>
                        <Tooltip title={`${st.checked ? 'Clear' : 'Grant'} ${a.label.toLowerCase()} on every feature`}>
                          <Checkbox size="small" checked={st.checked} indeterminate={st.indeterminate}
                            onChange={(e) => setColumnAll(a.key, e.target.checked)} sx={{ p: 0.25 }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  );
                })}
                <TableCell align="center" sx={{ width: 72 }}>All</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleFeatures.map((feat) => {
                const p = matrix[feat.key] || {};
                const rs = rowState(feat.key);
                return (
                  <TableRow key={feat.key} hover sx={{ '& td': { py: 0.5 } }}>
                    <TableCell>
                      <Typography sx={{ fontSize: f.sm, fontWeight: 600, lineHeight: 1.3 }}>{feat.label}</Typography>
                      <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>{feat.description}</Typography>
                    </TableCell>
                    {ACTIONS.map((a) => (
                      <TableCell key={a.key} align="center">
                        <Checkbox size="small" checked={p[a.key] === true}
                          onChange={(e) => setPerm(feat.key, a.key, e.target.checked)}
                          inputProps={{ 'aria-label': `${feat.label}: ${a.label}` }} />
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Checkbox size="small" checked={rs.checked} indeterminate={rs.indeterminate}
                        onChange={(e) => setRowAll(feat.key, e.target.checked)}
                        inputProps={{ 'aria-label': `${feat.label}: full access` }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography sx={{ fontSize: f.xs, color: 'text.secondary', mt: 1.5 }}>
          Create/Edit and Delete include View. Summary: <strong>{summary}</strong>.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ fontSize: f.sm }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ fontSize: f.sm }}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          {isEdit ? 'Save Role' : 'Create Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleDialog;
